import {
    Component,
    Input,
    OnInit,
    computed,
    Output,
    EventEmitter,
    OnChanges,
    SimpleChanges,
    signal,
    TemplateRef,
    ViewChild,
    ChangeDetectorRef,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { startWith } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatSelect } from '@angular/material/select';
import { isArray } from 'lodash-es';

@Component({
    selector: 'lh-dropdown-selector',
    standalone: false,
    templateUrl: './lh-dropdown-selector.component.html',
    styleUrls: ['./lh-dropdown-selector.component.scss'],
})
export class LhDropdownSelectorComponent implements OnInit, OnChanges {
    @Input() title: string = 'LH Filter';
    @Input() items: any[] = [];
    @Input() selectedValue: any = null;
    @Input() itemTemplate?: TemplateRef<any>;
    @Input() panelTemplate?: TemplateRef<any>;
    @Input() relatedItems: any[] = [];

    @Input() applyMode: boolean = false;

    @Output() selectionChange = new EventEmitter<any>();
    @Output() closed = new EventEmitter<any>();

    @ViewChild(MatSelect, { static: false }) matSelect!: MatSelect;

    public selectedItem: FormControl<any> = new FormControl();
    public searchFilterCtrl: FormControl<string> = new FormControl<string>('', {
        nonNullable: true,
    });

    private previousSelection: any[] = ['All'];
    private currentSelection: any[] = [];
    private initialSelection: any[] = [];
    private wasApplied = false;

    $items = signal<any[]>([]);

    $searchControlsChanges = toSignal<string>(
        this.searchFilterCtrl.valueChanges.pipe(startWith(''))
    );

    $filteredItems = computed(() => {
        const search = (this.$searchControlsChanges() || '').toLowerCase();
        const currentItems = this.$items();

        if (!search) {
            return currentItems;
        }

        return currentItems.filter((item) =>
            item.toLowerCase().includes(search)
        );
    });

    constructor(private cdr: ChangeDetectorRef) {}

    ngOnInit(): void {
        this.$items.set(this.items || []);

        const initial = this.selectedValue
            ? Array.isArray(this.selectedValue)
                ? this.selectedValue
                : [this.selectedValue]
            : ['All'];

        this.selectedItem.setValue(initial);
        this.initialSelection = [...initial];
        this.previousSelection = [...initial];
        this.currentSelection = [...initial];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['items']) {
            this.$items.set(this.items || []);

            if (!changes['selectedValue'] && this.selectedItem.value) {
                const currentVal = [...this.selectedItem.value];
                setTimeout(() => {
                    this.selectedItem.setValue(currentVal, {
                        emitEvent: false,
                    });
                    this.cdr.detectChanges();
                });
            }
        }

        if (changes['selectedValue'] && !this.wasApplied) {
            const val = this.selectedValue
                ? Array.isArray(this.selectedValue)
                    ? this.selectedValue
                    : [this.selectedValue]
                : null;

            if (!val) return;

            if (changes['selectedValue'].firstChange) {
                this.selectedItem.setValue(val, { emitEvent: false });
                this.previousSelection = [...val];
                this.currentSelection = [...val];
            } else {
                Promise.resolve().then(() => {
                    this.selectedItem.setValue(val, { emitEvent: false });
                    this.previousSelection = [...val];
                    this.currentSelection = [...val];
                    this.cdr.detectChanges();
                });
            }
        }
    }

    onSelectionChangeInternal() {
        this.currentSelection = Array.isArray(this.selectedItem.value)
            ? [...this.selectedItem.value]
            : [];

        const prev = Array.isArray(this.previousSelection)
            ? this.previousSelection
            : [];

        const hasAllNow = this.currentSelection.includes('All');
        const hadAllBefore = prev.includes('All');

        const addedAll = !hadAllBefore && hasAllNow;
        const hasOtherItems = this.currentSelection.some(
            (item) => item !== 'All'
        );

        if (addedAll) {
            this.selectedItem.setValue(['All'], { emitEvent: false });
            this.currentSelection = ['All'];
        } else if (hasAllNow && hasOtherItems) {
            const filtered = this.currentSelection.filter(
                (item) => item !== 'All'
            );
            this.selectedItem.setValue(filtered, { emitEvent: false });
            this.currentSelection = filtered;
        }
        this.previousSelection = [...this.currentSelection];
    }

    applySelection(event?: MouseEvent) {
        let selectedValues = Array.isArray(this.selectedItem.value)
            ? [...this.selectedItem.value]
            : [];

        let finalSelection =
            selectedValues.length > 1
                ? selectedValues.filter((item) => item !== 'All')
                : selectedValues;

        if (finalSelection.length === 0) finalSelection = ['All'];

        this.wasApplied = true;
        this.searchFilterCtrl.setValue('');
        this.initialSelection = [...finalSelection];
        this.previousSelection = [...finalSelection];
        this.currentSelection = [...finalSelection];
        this.selectedItem.setValue(finalSelection, { emitEvent: false });

        this.selectionChange.emit(finalSelection);
        if (this.matSelect) this.matSelect.close();
    }

    cancelSelection(event?: MouseEvent) {
        this.searchFilterCtrl.setValue('');
        this.selectedItem.setValue([...this.initialSelection], {
            emitEvent: false,
        });
        this.currentSelection = [...this.initialSelection];
        this.previousSelection = [...this.initialSelection];
        if (this.matSelect) {
            this.matSelect.close();
        }
    }

    onSelectClosed() {
        if (this.wasApplied) {
            this.wasApplied = false;
            return;
        }

        this.searchFilterCtrl.setValue('');
        this.selectedItem.setValue([...this.initialSelection], {
            emitEvent: false,
        });
        this.currentSelection = [...this.initialSelection];
        this.previousSelection = [...this.initialSelection];
    }

    onSelectOpened(opened: boolean) {
        if (opened) {
            this.initialSelection = Array.isArray(this.selectedItem.value)
                ? [...this.selectedItem.value]
                : [];
        }
    }

    isItemDisabled(item: any): boolean {
        return (
            this.relatedItems?.length > 0 &&
            item !== 'All' &&
            !this.relatedItems.includes(item)
        );
    }
}
